import Player from "@vimeo/player"

import {getTranslationsByNamespace} from "../utils/translations"

const VIDEO_TYPE_VIMEO = 'vimeo'
const VIDEO_TYPE_WP_MEDIA = 'wpmedia'
const VIDEO_TYPE_MP4 = 'mp4'

/**
 * class VideoModal
 * When trigger element is clicked, a popup video player is rendered for either Vimeo or MP4.
 */
class VideoModal {

    /**
     * Define data attribute names
     */
    DATA_ATTRIBUTE_MODAL_ID = 'data-modal-id'
    DATA_ATTRIBUTE_MODAL_ACTIVE = 'data-modal-active'

    /**
     * VideoModal constructor
     * @param $triggerElement {Element}
     */
    constructor($triggerElement = null) {
        if ($triggerElement === null || !$triggerElement instanceof Element) throw new Error('Missing required parameter `$triggerElement` in class VideoModal or given parameter was not a valid Element.')

        this.state = {
            $triggerElement,
            translations: getTranslationsByNamespace('ajax_object'),
            videoIdentifier: $triggerElement.dataset.videoIdentifier,
            videoContainerId: $triggerElement.dataset.modalContainerId,
            videoType: $triggerElement.dataset.videoType,
            vimeoPlayerInstance: null,
            primaryColor: $triggerElement.dataset.primaryColor,
            secondaryColor: $triggerElement.dataset.secondaryColor,
            active: false,
            loading: false,
            $modalElement: null,
            $videoContainerElement: null,
            $videoPlayerElement: null, //<video> tag
            $closeElement: null,
            $backgroundElement: null,
            readyEvent: null,
            errored: false
        }

        //bail if no video ID or type
        if (!this.state.videoIdentifier || !this.state.videoType) return

        //bind click event on trigger element
        this.state.$triggerElement.addEventListener('click', this.handleTriggerClick.bind(this))

        //close on ESC press on desktop
        window.addEventListener("keyup", this.handleKeyPress.bind(this))
    }

    /**
     * Handle trigger click
     * @param event
     */
    handleTriggerClick(event) {
        if (event) event.preventDefault()
        if (!this.state.active && !this.state.loading) {

            //start loading
            this.setLoading(true)

            //render video player based on video type
            switch (this.state.videoType) {
                case VIDEO_TYPE_VIMEO:

                    //create modal if it does not exist yet
                    if (this.exists() === false) this.renderVimeo()

                    if (this.state.errored) {
                        this.setLoading(false)
                        alert(`Vimeo video with ID ${this.state.videoIdentifier} was not found.`)
                        return
                    }

                    //load player)
                    this.state.vimeoPlayerInstance = new Player(this.state.$videoContainerElement, {id: this.state.videoIdentifier})

                    //handle player ready
                    this.state.vimeoPlayerInstance.ready().then(() => {

                        //add css class to iframe
                        this.state.vimeoPlayerInstance.element.classList.add('video-modal__video')

                        //stop loading and play the video
                        setTimeout(() => {
                            this.setLoading(false)
                            this.setActive(true)
                        }, 250)
                    })

                    //handle player error
                    this.state.vimeoPlayerInstance.ready().catch(err => {
                        this.state.errored = true
                        this.setLoading(false)
                        alert(`Vimeo video with ID ${this.state.videoIdentifier} was not found.`)
                    })

                    break
                case VIDEO_TYPE_WP_MEDIA:
                case VIDEO_TYPE_MP4:

                    //create modal if it does not exist yet
                    if (this.exists() === false) this.renderMp4Video()

                    //activate modal when video is loaded
                    if (this.state.$videoPlayerElement) {
                        this.state.$videoPlayerElement.load()
                        this.state.$videoPlayerElement.addEventListener('loadeddata', () => {
                            setTimeout(() => {
                                this.setLoading(false)
                                this.setActive(true)
                            }, 250)
                        })
                    }
                    break
                default:
                    this.setLoading(false)
                    console.log(`Video type ${this.state.videoType} was not recognized..`)
            }
        }
    }

    /**
     * Handle escape key pressed
     */
    handleKeyPress() {
        if (this.state.active && !this.state.loading && event.keyCode === 27) this.setActive(false)
    }

    /**
     * Util to check if modal exists
     * @return {boolean}
     */
    exists() {
        switch (this.state.videoType) {
            case VIDEO_TYPE_VIMEO:
                return this.state.$modalElement !== null && this.state.vimeoPlayerInstance !== null && this.state.$closeElement !== null && this.state.$backgroundElement !== null
            case VIDEO_TYPE_WP_MEDIA:
            case VIDEO_TYPE_MP4:
                return this.state.$modalElement !== null && this.state.$videoPlayerElement !== null && this.state.$closeElement !== null && this.state.$backgroundElement !== null
            default:
                return false
        }
    }

    /**
     * Render Vimeo player
     */
    renderVimeo() {
        //create HTML
        const videoHtml = `<div class="video-modal__video-container"></div>`
        const modalHtml = this.getVideoModalHtml(videoHtml)

        //add HTML to DOM
        document.body.insertAdjacentHTML('beforeend', modalHtml)

        //set reference to modal in state
        this.state.$modalElement = document.querySelector(`[${this.DATA_ATTRIBUTE_MODAL_ID}="${this.state.videoContainerId}"]`)
        this.state.$videoContainerElement = this.state.$modalElement.querySelector(".video-modal__video-container")
        this.state.$closeElement = this.state.$modalElement.querySelector(".video-modal__close")
        this.state.$backgroundElement = this.state.$modalElement.querySelector(".video-modal__background")

        //bind events for close and background click
        this.state.$closeElement.addEventListener("click", () => this.setActive(false))
        this.state.$backgroundElement.addEventListener("click", () => this.setActive(false))
    }

    /**
     * Render MP4 video player
     */
    renderMp4Video() {

        const videoHtml = `
            <video class="video-modal__video" controls>
                <source src="${this.state.videoIdentifier}" type="video/mp4">
            </video>
        `

        //create html
        const modalHtml = this.getVideoModalHtml(videoHtml);

        //add HTML to DOM
        document.body.insertAdjacentHTML('beforeend', modalHtml)

        //set reference to modal in state
        this.state.$modalElement = document.querySelector(`[${this.DATA_ATTRIBUTE_MODAL_ID}="${this.state.videoContainerId}"]`)
        this.state.$videoPlayerElement = this.state.$modalElement.querySelector(".video-modal__video")
        this.state.$closeElement = this.state.$modalElement.querySelector(".video-modal__close")
        this.state.$backgroundElement = this.state.$modalElement.querySelector(".video-modal__background")

        //bind events for close and background click
        this.state.$closeElement.addEventListener("click", () => this.setActive(false))
        this.state.$backgroundElement.addEventListener("click", () => this.setActive(false))
    }

    /**
     * Get video HTML
     * @param videoHtml
     * @returns {string}
     */
    getVideoModalHtml(videoHtml = '<h1>No video given</h1>') {
        return `
            <div class="video-modal" ${this.DATA_ATTRIBUTE_MODAL_ID}="${this.state.videoContainerId}" ${this.DATA_ATTRIBUTE_MODAL_ACTIVE}="false">

                <div class="video-modal__wrapper">

                    <div class="video-modal__close" style="background-color: ${this.state.secondaryColor}">

                        <svg class="video-modal__close-icon"
                            viewBox="0 0 33 33"
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.291 32l15.296-15.413L1 1l15.587 15.587L32 1.291 16.587 16.587 31.71 31.71"
                                stroke="currentColor"
                                stroke-width="3"
                                fill="none"
                                fill-rule="evenodd"/>
                        </svg>

                        <span class="video-modal__close-text">
                            ${this.state.translations.get("videoModalClose", "Close")}
                        </span>

                    </div>

                    ${videoHtml}

                </div>

                <div class="video-modal__background"></div>

            </div>
        `
    }

    /**
     * Set loading state
     * @param {boolean} newState
     */
    setLoading(newState = false) {
        this.state.loading = newState
        this.state.$triggerElement.dataset.modalLoading = newState ? "true" : "false"
    }

    /**
     * Set active state
     * @param {boolean} newState
     */
    setActive(newState = false) {
        this.state.active = newState

        //alter activation for trigger and modal
        this.state.$triggerElement.dataset.modalActive = newState === true ? "true" : "false"
        this.state.$modalElement.dataset.modalActive = newState === true ? "true" : "false"

        //play or pause Vimeo video based on new state
        if (this.state.vimeoPlayerInstance) {
            if (newState === true) {
                this.state.vimeoPlayerInstance.play()
            } else {
                this.state.vimeoPlayerInstance.pause()
                this.state.vimeoPlayerInstance.unload()
            }
        }

        //play or pause mp4 video based on new state
        if (this.state.$videoPlayerElement) {
            newState === true ? this.state.$videoPlayerElement.play() : this.state.$videoPlayerElement.pause()
        }
    }
}

export default VideoModal